import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { FormSnippet } from './FormSnippet';
import type { SnippetsFieldValues } from './SnippetsFieldValues';

type AnyFormSnippet<TModel> = FormSnippet<TModel, any, any, any, any>;

/**
 * FormSnippetGroup represents a group of form snippets that are displayed together at once.
 * That can be a tab, collapsible section or something alike.
 *
 * The main purpose of grouping the snippets this way is showing validation errors:
 * when an error occurs in one of the snippets, the group's .show() method is called.
 */
export type FormSnippetGroup<
  TModel,
  TSnippetKey extends string,
  TGroupDisplayControl = void,
  TSnippets extends Record<TSnippetKey, AnyFormSnippet<TModel>> = Record<
    TSnippetKey,
    AnyFormSnippet<TModel>
  >,
> = {
  // Snippet arguments for which any is used aren't a concern of the group.
  // TSnippets is there to infer individual snippet field value types.
  // Record<TSnippetKey, ...> is there to infer TSnippetKey properly.
  snippets: TSnippets & Record<TSnippetKey, AnyFormSnippet<TModel>>;
  render: (args: {
    renderSnippet: (key: TSnippetKey) => ReactNode;
    form: UseFormReturn<SnippetsFieldValues<TSnippets>>;
  }) => ReactNode;
} & (TGroupDisplayControl extends void
  ? {
      show?: never;
    }
  : {
      /**
       * Show this group in the UI (change to proper tab, expand the section, etc.)
       */
      show: (control: TGroupDisplayControl) => void;
    });

/**
 * Used to infer type arguments.
 */
export const createFormSnippetGroup = <
  TModel,
  TSnippetKey extends string,
  TGroupDisplayControl = void,
  TSnippetFieldValues extends Record<string, any> = Record<string, any>,
>(
  args: FormSnippetGroup<
    TModel,
    TSnippetKey,
    TGroupDisplayControl,
    TSnippetFieldValues
  >,
): FormSnippetGroup<
  TModel,
  TSnippetKey,
  TGroupDisplayControl,
  TSnippetFieldValues
> => args;
